import type { BillingState } from "../billing/billingStore";

/** Card rarity + viewer chrome. Three values only. */
export type ProfileTier = "vip" | "paid" | "free";

/** @deprecated Use ProfileTier. Kept so older imports still type-check. */
export type MatchChromePlan = ProfileTier;

const TIER_RANK: Record<ProfileTier, number> = {
  free: 0,
  paid: 1,
  vip: 2,
};

/**
 * Map any stored / legacy plan string onto vip | paid | free.
 * Accepts a string, a profile-like `{ plan }`, or undefined.
 */
export function tierOf(input?: unknown): ProfileTier {
  let raw = "";
  if (typeof input === "string") {
    raw = input;
  } else if (input && typeof input === "object" && "plan" in input) {
    const plan = (input as { plan?: unknown }).plan;
    if (typeof plan === "string") raw = plan;
  }
  const key = raw.trim().toLowerCase();
  if (key === "vip") return "vip";
  if (
    key === "paid" ||
    key === "subscriber" ||
    key === "trial" ||
    key === "premium" ||
    key === "plus"
  ) {
    return "paid";
  }
  return "free";
}

export function tierRank(tier: ProfileTier): number {
  return TIER_RANK[tier];
}

/** Highest card in a pack — drives ceremony intensity, not viewer billing. */
export function packIntensity(profiles: ReadonlyArray<{ plan?: unknown }>): ProfileTier {
  let best: ProfileTier = "free";
  for (const p of profiles) {
    const next = tierOf(p);
    if (TIER_RANK[next] > TIER_RANK[best]) best = next;
  }
  return best;
}

export function rayCountFor(tier: ProfileTier): number {
  if (tier === "vip") return 16;
  if (tier === "paid") return 10;
  return 6;
}

export function packDurationMs(tier: ProfileTier, reduced: boolean): number {
  if (reduced) return 1600;
  if (tier === "vip") return 6400;
  if (tier === "paid") return 5200;
  return 4800;
}

/**
 * Closed-test demo: if the weekly four is missing a painted tier that the
 * pool still has, swap a duplicate-tier card so bronze / silver / gold all
 * show. Real member ranking is unchanged — callers opt in.
 */
export function mixDemoTiers<T extends { id: string; plan?: unknown }>(
  selected: T[],
  pool: readonly T[]
): T[] {
  if (selected.length === 0) return selected;
  const have = new Set(selected.map((p) => tierOf(p)));
  const used = new Set(selected.map((p) => p.id));
  const next = selected.slice();
  for (const tier of ["vip", "paid", "free"] as const) {
    if (have.has(tier)) continue;
    const add = pool.find((p) => !used.has(p.id) && tierOf(p) === tier);
    if (!add) continue;
    const counts = new Map<ProfileTier, number>();
    for (const p of next) {
      const t = tierOf(p);
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    const dupIdx = next.findIndex((p) => (counts.get(tierOf(p)) ?? 0) > 1);
    if (dupIdx < 0) continue;
    used.delete(next[dupIdx].id);
    next[dupIdx] = add;
    used.add(add.id);
    have.add(tier);
  }
  // Keep one gold and one silver for a later shuffle when the pool still
  // has eight people — otherwise week one spends both VIPs.
  for (const tier of ["vip", "paid"] as const) {
    const extra = next.filter((p) => tierOf(p) === tier);
    const restHas = pool.some((p) => !used.has(p.id) && tierOf(p) === tier);
    if (extra.length <= 1 || restHas) continue;
    const spare = pool.find((p) => !used.has(p.id) && tierOf(p) !== tier);
    if (!spare) continue;
    const dupIdx = next.findIndex((p) => tierOf(p) === tier);
    if (dupIdx < 0) continue;
    used.delete(next[dupIdx].id);
    next[dupIdx] = spare;
    used.add(spare.id);
  }
  return next;
}

/**
 * Viewer's own entitlement — feature gating only.
 * Write this to `data-viewer-plan`, never to card `data-plan`.
 */
export function chromePlanFromBilling(billing: BillingState): ProfileTier {
  if (billing.plan === "vip" || billing.unlimitedCredits) return "vip";
  if (
    billing.plan === "subscriber" ||
    billing.plan === "trial" ||
    billing.canDailySwap
  ) {
    return "paid";
  }
  return "free";
}
